import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Container,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  ArrowUpward as PromoteIcon,
  ArrowDownward as DemoteIcon,
  VpnKey as PasswordIcon,
} from "@mui/icons-material";

import { useUserStore } from "store/UsersStore";

const UsersTab = () => {
  const { users, user_stats, loading, fetchUsers, deleteUser, adminPromoteUser, adminDemoteUser, updatePasswordUser } = useUserStore();

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const openMenu = (event, user) => {
    setSelectedUser(user);
    setMenuAnchor(event.currentTarget);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setSelectedUser(null);
  };

  const closeSnackbar = () => setSnackbar({ ...snackbar, open: false });

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleAction = (actionFn) => {
    if (!selectedUser) return;
    actionFn(selectedUser.id);
    closeMenu();
  };

  const handlePasswordChange = () => {
    if (!selectedUser) return;
    const password = prompt(`Enter new password for ${selectedUser.username}:`);
    if (!password) return;
    updatePasswordUser(selectedUser.id, { password });
    closeMenu();
  };

  return (
    <Box>
      {loading ? (
        <CircularProgress />
      ) : users ? (
        <Container>
          <Typography color="text.primary" variant="h4" gutterBottom>Users</Typography>
          <Typography color="text.primary" variant="h6" gutterBottom>Total Users: {users.length}</Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Roles</TableCell>
                  <TableCell>Simple UI</TableCell>
                  <TableCell>Searches</TableCell>
                  <TableCell>Downloads</TableCell>
                  <TableCell>Logins</TableCell>
                  <TableCell>Emails</TableCell>
                  <TableCell>Library</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.id}</TableCell>
                    <TableCell>{user.first_name} {user.last_name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.roles}</TableCell>
                    <TableCell>{user.simple_ui ? "Yes" : "No"}</TableCell>
                    <TableCell>{user_stats[user.id].searches || 0}</TableCell>
                    <TableCell>{user_stats[user.id].downloads || 0}</TableCell>
                    <TableCell>{user_stats[user.id].logins || 0}</TableCell>
                    <TableCell>{user_stats[user.id].emails_sent || 0}</TableCell>
                    <TableCell>{user_stats[user.id].library_size || 0}</TableCell>
                    <TableCell align="center">
                      <IconButton onClick={(e) => openMenu(e, user)}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Action Menu */}
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={closeMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem onClick={() => handleAction(adminPromoteUser)}>
              <ListItemIcon><PromoteIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Promote</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleAction(adminDemoteUser)}>
              <ListItemIcon><DemoteIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Demote</ListItemText>
            </MenuItem>
            <MenuItem onClick={handlePasswordChange}>
              <ListItemIcon><PasswordIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Change Password</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleAction(deleteUser)}>
              <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          </Menu>
        </Container>
      ) : (
        <Typography>No users found.</Typography>
      )}
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={closeSnackbar}>
        <Alert onClose={closeSnackbar} severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default UsersTab;
